import React from 'react';
import { BlogArticle } from '../../types/blog';

interface BlogCardProps {
  article: BlogArticle;
  onNavigate: (path: string, view: string, tab?: string) => void;
  headingLevel?: 'h2' | 'h3';
}

export const BlogCard: React.FC<BlogCardProps> = ({ article, onNavigate, headingLevel = 'h2' }) => {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onNavigate(`/blog/${article.slug}/`, 'blog-article', article.slug);
  };

  const HeadingTag = headingLevel;

  return (
    <article className="group flex flex-col bg-white dark:bg-[#182332] rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-xs hover:shadow-lg hover:border-[#087F9C] dark:hover:border-[#39BFD3] transition-all duration-300 overflow-hidden">
      {/* Thumbnail */}
      <a
        href={`/blog/${article.slug}/`}
        onClick={handleClick}
        className="relative block aspect-[16/9] w-full overflow-hidden bg-slate-100 dark:bg-slate-800 border-b border-slate-100 dark:border-slate-700/60"
        aria-label={`Read article: ${article.title}`}
      >
        <img
          src={`/assets/blog/${article.svgVisualId}.svg`}
          alt={article.svgAlt}
          loading="lazy"
          className="w-full h-full object-contain p-2 group-hover:scale-102 transition-transform duration-300"
        />
        <div className="absolute top-3 left-3">
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wide bg-[#10283D]/90 text-[#39BFD3] backdrop-blur-xs shadow-xs border border-slate-700/40">
            {article.category}
          </span>
        </div>
      </a>

      {/* Card Content */}
      <div className="flex-1 p-5 sm:p-6 flex flex-col justify-between">
        <div className="space-y-2.5">
          <div className="flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400 font-medium">
            <span>{article.date}</span>
            <span className="flex items-center gap-1">
              <svg
                className="w-3.5 h-3.5 text-[#087F9C] dark:text-[#39BFD3]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              {article.readingTime}
            </span>
          </div>

          <HeadingTag className="font-display font-bold text-lg sm:text-xl text-[#10283D] dark:text-white group-hover:text-[#087F9C] dark:group-hover:text-[#39BFD3] transition-colors line-clamp-2 leading-snug">
            <a href={`/blog/${article.slug}/`} onClick={handleClick}>
              {article.title}
            </a>
          </HeadingTag>

          <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 line-clamp-3 leading-relaxed">
            {article.excerpt}
          </p>
        </div>

        {/* Footer Link & Calculator Link */}
        <div className="mt-5 pt-4 border-t border-slate-100 dark:border-slate-700/60 flex items-center justify-between text-xs">
          <a
            href={`/blog/${article.slug}/`}
            onClick={handleClick}
            className="font-bold text-[#087F9C] dark:text-[#39BFD3] group-hover:translate-x-0.5 transition-transform flex items-center gap-1"
          >
            Read Guide <span>&rarr;</span>
          </a>

          <a
            href={article.calculatorLink}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onNavigate(article.calculatorLink, 'calculators', 'water');
            }}
            className="text-[11px] font-semibold text-slate-500 hover:text-[#087F9C] dark:text-slate-400 dark:hover:text-[#39BFD3] transition-colors truncate max-w-[140px]"
            title={`Use calculator: ${article.calculatorName}`}
          >
            ⚡ {article.calculatorName}
          </a>
        </div>
      </div>
    </article>
  );
};
